import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { Box, Divider, Stack, Typography } from '@mui/material';

import { AccordionButton } from 'components/Button';
import { ExcludedRolesTab, RatesTab, SettingsTab } from 'features/procurementSetup';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';

import { paths } from 'routes';

import { useDirect } from '../api/getById';
import { DetailViewTabList } from 'components/TabList';
import { DetailsTab } from '../components/DetailsTab';
import { BuyersTab } from '../components/BuyersTab';
import { SuppliersTab } from '../components/SuppliersTab';
import { EditForm } from '../components/EditForm';
import { useDirectCompanyCount } from '../api/getCompanyCount';
import { useDirectFilesCount } from '../api/getFilesCount';
import { useDirectSuppliers } from '../api/getSuppliers';
import { useDirectsSupplierFilesCountQueries } from '../api/getSupplierFilesCount';
import { StatusToggleButton } from '../components/StatusToggleButton';
import { useAuth } from 'lib/auth';
import { FilesTab } from '../components/FilesTab';
import { PremiumBanner } from '../../subscriptions/components/PremiumBanner';
import { Loader } from 'components/Loader';
import { IconWrapper } from 'components/Icon';
import { BoostToggleButton } from '../components/BoostToogleButton';
import dayjs from 'dayjs';
import { ImageAvatar } from 'components/Avatar';

export const DetailsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const currentTab = queryParams.get('tab') || 'details';
  const [ loading, setLoading ] = useState(false);
  const { account, user } = useAuth();
  let { id } = useParams();
  const [tab, setTab] = useState(currentTab);
  const [timer, setTimer] = useState(null);

  const { data: direct, refetch: refetchDirect, error, isError,  isLoading: directLoading, isFetched: isFetched1 } =
    useDirect(id);
  const {
    //@ts-ignore
    data: { count: buyersCount, isLoading: buyersCountLoading, isFetched: isFetched2 },
    refetch: refetchBuyersCount,
  } = useDirectCompanyCount(id, 'buyer');
  const {
    //@ts-ignore
    data: { count: suppliersCount, isLoading: suppliersCountLoading, isFetched: isFetched3 },
  } = useDirectCompanyCount(id, 'supplier');
  const {
    //@ts-ignore
    data: { count: filesCount, isLoading: filesCountLoading, isFetched: isFetched4 },
  } = useDirectFilesCount(id);

  const {
    data: { suppliers },
    isLoading: isLoadingSuppliers,
    isFetched: isFetched5,
    refetch: refetchSuppliersFiles,
  } = useDirectSuppliers(id, {
    includeInactive: account.super_admin ? true : false,
    limit: 200,
    queryKey: [`framework-agreement-${id}-files-suppliers`],
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
    },
  });

  const tcIDs = suppliers.map(supplier => supplier.tc_id);
  const filesCountQueries = useDirectsSupplierFilesCountQueries(id, tcIDs);
  const suppliersFilesCount = filesCountQueries.reduce((acc, query, index) => {
    if (tcIDs[index] != null) {
      acc[tcIDs[index]] = query.data?.count || 0;
    }

    return acc;
  }, {});
  const isLoadingSuppleirsFilesCount = filesCountQueries.some(query => query.isLoading);
  const allSuppliersFilesFetched = filesCountQueries.every(query => query.isFetched);

  const { suppliersFilesTotal, isSuppliersFilesTotalReady } = allSuppliersFilesFetched ? filesCountQueries.length === 0 ?
  { suppliersFilesTotal: 0, isSuppliersFilesTotalReady: true }
  :
  filesCountQueries.reduce(
    (acc, query, index, array) => {
      const count = query.data?.count || 0;
      acc.suppliersFilesTotal += count;

      if (index === array.length - 1 && allSuppliersFilesFetched) {
        acc.isSuppliersFilesTotalReady = true;
      }

      return acc;
    },
    { suppliersFilesTotal: 0, isSuppliersFilesTotalReady: false }
  ) :
  { suppliersFilesTotal: 0, isSuppliersFilesTotalReady: false };

  const switchTab = (_, newTab) => {
    navigate(`?tab=${newTab}`, { replace: true });
  };

  const isLoading = directLoading || buyersCountLoading || suppliersCountLoading || filesCountLoading || isLoadingSuppliers || isLoadingSuppleirsFilesCount;
  const isFetched = isFetched1 || isFetched2 || isFetched3 || isFetched4 || isFetched5 || allSuppliersFilesFetched;

  const totalFiles = isFetched && isSuppliersFilesTotalReady ? filesCount + suppliersFilesTotal : 0; 

  const today = dayjs();
  const lastOfferDate = dayjs(direct?.last_offer_date);
  const isOpen = isFetched && lastOfferDate.isAfter(today);

  useEffect(() => {  
    if (isLoading) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isFetched) {    
    if (loading) {      
      setTimeout(() => setLoading(false), 1000);
    }
  }

  useEffect(() => {
    setTab(currentTab);
  }, [currentTab]);

  if (!loading && isError && error.message === "Object doesn't exist") {
    return <div>{error?.message}</div>;
  }

  return (
    <ContentLayout>
      {isLoading && !loading ? (
        <></>
      )
      : (
        loading ? (
          <Loader style={{marginTop: "40vh"}}/>
        )
        : (
          !loading && isError ? (
            <PremiumBanner sx={{my: 0}} message={`This Direct is only available for Premium members. Click here for more information`} handleSuccess={refetchDirect} currentLevel={user.Company.subscription_level} />
          ) : (
            <Stack height="100%">
              <BreadCrumbs
                items={[
                  { name: '', to: paths.directs },
                  { name: 'Directs', to: paths.directs },
                  { name: `${direct.MainBuyer.Name}`, to: `${paths.buyersDetailsView(direct.MainBuyer.company_id)}?tab=directs` },
                  { name: direct?.direct_name ?? 'Details View' },
                ]}
              />
              <Box
                height={50}
                sx={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  paddingBottom: 1.5,
                  paddingTop: 1,
                }}
              >
                <Stack direction="row" pl={4} gap={2} alignItems="center">
                  <ImageAvatar img={direct?.MainBuyer?.square_logo_url || ''} name={direct?.MainBuyer?.Name || ''} sx={{alignSelf: 'center'}}/>
                  <Stack>
                    <Typography variant="h3">
                      <Stack flex={1} flexDirection={"row"} gap={1}>
                        {direct?.direct_name || ''}
                        {direct?.promoted && (
                          <IconWrapper
                            icon="rocket"
                            sx={({ palette }) => ({ svg: { width: 16, height: 16, stroke: palette.primary.main, color: palette.primary.main }, display: "flex" })}
                            height={16}
                            display={"flex"}
                            alignSelf={"center"}
                          />
                        )}
                        {account.super_admin && (
                          <Stack flex={1} sx={{justifyContent: "center"}}>
                            <Box
                              borderRadius="100%"
                              width={8}
                              height={8}
                              border="1px solid"
                              borderColor={direct?.Status === 'active' ? 'success.light' : 'error.light'}
                              bgcolor={direct?.Status === 'active' ? 'success.light' : 'error.light'}
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Typography>
                    <Typography variant='caption' fontWeight={600}>
                      {`${direct.MainBuyer.Name} (${direct.ref_number})`}
                    </Typography>
                  </Stack>
                </Stack>
                {(account.super_admin || (user.company_id === direct.main_buyer_id && user.Company.is_premium && user.Company.subscription_level === "gold")) && (
                  <AccordionButton label="Actions" sx={{ width: 130 }}>
                    <Stack gap={0.5}>
                      {account.super_admin && (
                        <>
                          <EditForm direct={direct} refetch={refetchDirect}/>

                          <StatusToggleButton
                            refetch={refetchDirect}
                            status={direct?.Status}
                          />
                        </>
                      )}

                      <BoostToggleButton
                        refetch={refetchDirect}
                        boosted={direct?.promoted}
                      />
                    </Stack>
                  </AccordionButton>
                )}
              </Box>
              <DetailViewTabList
                value={tab}
                onChange={switchTab}
                tabs={[
                  { label: 'Details' },
                  { label: 'Buyers', count: buyersCount },
                  { label: 'Blacklist', count: suppliersCount },
                  { label: 'Files', count: totalFiles },
                  { label: 'Rates' },
                  ...(account.super_admin
                    ? [{ label: 'Excluded roles', value: 'excluded_roles' }, { label: 'Settings' }]
                    : []),
                ]}
              />

              <Stack
                px={4}
                py={2}
                bgcolor="background.paper"
                borderTop="1px solid"
                borderColor="border.main"
                gap={1}
                height="100%"
              >
                {error && (
                  <PremiumBanner message={`This Direct is only available for Premium members. Click here for more information`} handleSuccess={refetchDirect} currentLevel={user.Company.subscription_level} />
                  // <div>{error?.message}</div>
                )}
                {!error && (
                  <>
                {tab === "details" && (
                  <DetailsTab currentTab={tab} direct={direct} />
                )}

                {tab === "buyers" && (
                  <BuyersTab
                    currentTab={tab}
                    refetchBuyersCount={refetchBuyersCount}
                    count={buyersCount}
                  />
                )}

                {tab === "blacklist" && (
                  <SuppliersTab currentTab={tab} refetchSuppliersFiles={refetchSuppliersFiles} count={suppliersCount} />
                )}

                {tab === "files" && (
                  <FilesTab
                    currentTab={tab}
                    suppliers={suppliers}
                    suppliersFilesCount={suppliersFilesCount}
                    generalFilesCount={filesCount}
                    boosted={direct?.promoted}
                    isOpen={isOpen}
                  />
                )}

                <RatesTab currentTab={tab} resource="directs" id={id} procurementName={direct?.direct_name} />
                {tab === "excluded_roles" && (
                  <ExcludedRolesTab currentTab={tab} resource="directs" id={id} procurementName={direct?.direct_name} />
                )}

                {tab === "settings" && (
                  <SettingsTab currentTab={tab} resource="directs" id={id} procurementName={direct?.direct_name} />
                )}
                </>
              )}
              </Stack>
            </Stack>
          )
        )
      )}
    </ContentLayout>
  );
};
